import { FC, JSX, useEffect, useState } from 'react';
import { WishlistModel, WishlistContext } from './WishlistContext.ts';
import { Property } from '@model';
import { useProperties } from './PropertiesContext';

const WISHLIST_STORAGE_KEY = 'wishlist';

export const WishlistProvider: FC<{ children: JSX.Element }> = ({
  children,
}) => {
  const { properties } = useProperties();
  const [wishlistMap, setWishlistMap] = useState<Map<number, Property>>(
    new Map(),
  );
  const [showModalProperty, setShowModalProperty] = useState(
    undefined as Property | undefined,
  );

  useEffect(() => {
    const storedIds = JSON.parse(
      localStorage.getItem(WISHLIST_STORAGE_KEY) || '[]',
    ) as number[];
    const newWishlistMap = new Map<number, Property>();
    storedIds.forEach((id) => {
      const property = properties.find((p) => p.id === id);
      if (property) {
        newWishlistMap.set(id, property);
      }
    });
    setWishlistMap(newWishlistMap);
  }, [properties]);

  const addToWishlist = (property: Property) => {
    if (wishlistMap.has(property.id)) return;

    const newWishlistMap = new Map(wishlistMap);
    newWishlistMap.set(property.id, property);
    setWishlistMap(newWishlistMap);
    localStorage.setItem(
      WISHLIST_STORAGE_KEY,
      JSON.stringify([...newWishlistMap.keys()]),
    );
    setShowModalProperty(property);
  };

  const removeFromWishlist = (id: number) => {
    const newWishlistMap = new Map(wishlistMap);
    newWishlistMap.delete(id);
    setWishlistMap(newWishlistMap);
    localStorage.setItem(
      WISHLIST_STORAGE_KEY,
      JSON.stringify([...newWishlistMap.keys()]),
    );
  };

  const isInWishlist = (id: number): boolean => wishlistMap.has(id);

  const model = {
    wishlist: Array.from(wishlistMap.values()),
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    hideWishlistModal: () => setShowModalProperty(undefined),
    wishlistModalProperty: showModalProperty,
  } as WishlistModel;

  return (
    <WishlistContext.Provider value={model}>
      {children}
    </WishlistContext.Provider>
  );
};
